import React, { FC, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { UserContext } from '@/contexts/UserContext';
import { useGetStreamKeys } from '@/adapters/stream-keys';
import { RunsheetSummary, useDeleteRunsheet, useListRunsheetsBulk } from '@/adapters/runsheet-v2';
import { RUNSHEET_NEW_URL, RUNSHEETS_URL } from '@/routePaths';

interface EnrichedRunsheet extends RunsheetSummary {
  streamKeyName: string;
}

const RunsheetsPage: FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { userState } = useContext(UserContext);
  const { data: streamKeyList, isLoading: streamKeysLoading } = useGetStreamKeys(userState?.role);
  const streamKeys = useMemo(() => streamKeyList?.items ?? [], [streamKeyList?.items]);
  const streamKeyNameById = useMemo(() => {
    const map = new Map<string, string>();
    streamKeys.forEach((sk) => map.set(sk.streamKey, sk.name || sk.streamKey));
    return map;
  }, [streamKeys]);

  // Single round-trip: server-side parallel fan-out across all streamKeys.
  // Avoids the browser's ~6 concurrent-request cap and the all-or-nothing wait.
  const streamKeyValues = useMemo(() => streamKeys.map((sk) => sk.streamKey), [streamKeys]);
  const { data: rawRunsheets = [], isLoading: runsheetsLoading } =
    useListRunsheetsBulk(streamKeyValues);

  const allRunsheets = useMemo<EnrichedRunsheet[]>(
    () =>
      rawRunsheets.map((r) => ({
        ...r,
        streamKeyName: streamKeyNameById.get(r.streamKey) || r.streamKey,
      })),
    [rawRunsheets, streamKeyNameById],
  );

  const anyQueryLoading = streamKeysLoading || runsheetsLoading;

  const deleteRunsheet = useDeleteRunsheet();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleDelete = async (runsheet: EnrichedRunsheet): Promise<void> => {
    const ok = window.confirm(
      `Delete runsheet "${runsheet.title}"? This soft-deletes it — past streams keep their history.`,
    );
    if (!ok) return;
    try {
      setPendingDeleteId(runsheet.runsheetId);
      await deleteRunsheet.mutateAsync({
        runsheetId: runsheet.runsheetId,
        streamKey: runsheet.streamKey,
      });
      enqueueSnackbar('Runsheet deleted', { variant: 'success' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Failed to delete runsheet', { variant: 'error' });
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Runsheets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Each runsheet belongs to a stream key. The most recently created one is applied
            automatically when a stream starts.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(RUNSHEET_NEW_URL)}
        >
          New Runsheet
        </Button>
      </Stack>

      {anyQueryLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading runsheets…
          </Typography>
        </Box>
      )}

      {!anyQueryLoading && allRunsheets.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            No runsheets yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a runsheet for one of your stream keys. When a stream starts, the most recent
            runsheet is applied automatically.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(RUNSHEET_NEW_URL)}
          >
            New Runsheet
          </Button>
        </Paper>
      )}

      {allRunsheets.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Stream key</TableCell>
                <TableCell>Rows</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allRunsheets.map((r, index) => {
                const isLatestOnStreamKey =
                  allRunsheets.findIndex((x) => x.streamKey === r.streamKey) === index;
                return (
                  <TableRow key={r.runsheetId} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{r.title}</Typography>
                        {isLatestOnStreamKey && (
                          <Chip label="Active" size="small" color="success" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {r.streamKeyName}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.rowCount}</TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{new Date(r.updatedAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`${RUNSHEETS_URL}/${r.runsheetId}`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(r)}
                            disabled={pendingDeleteId === r.runsheetId}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RunsheetsPage;
